import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { isAfter } from 'date-fns';
import { toast } from 'react-toastify';
import { FaHotel, FaInfoCircle } from 'react-icons/fa';
import { getExchangeRate } from 'redux/settingsSlice';
import { createBookingHotel, updateBookingHotel } from 'redux/booking/travel/travelBookingSlice';
import TinymceEditor from 'components/common/TinymceEditor';
import BookingUploadAttachment from '../../BookingUploadAttachment';
import { Drawer, Button, Alert, FormRow, CurrencySelect } from 'components/common';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const hint = 'tw-mt-1 tw-text-xs tw-text-slate-500 tw-flex tw-items-center tw-gap-1';

const HotelForm = ({ showModal, setshowModal, booking, hotel, transit }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, reset, watch } = useForm();
  const { usdExchangeRateData } = useSelector((state) => state.settings);
  const [files, setFiles] = useState([]);

  const dispatch = useDispatch();
  const showExchangeRate = booking.currency === 'USD';
  const bookingId = booking.id;
  const title = hotel ? 'Edit' : 'Create';

  useEffect(() => { if (!hotel) reset(); }, [hotel, reset]);
  useEffect(() => { if (showExchangeRate) dispatch(getExchangeRate()); }, [showExchangeRate, dispatch]);

  useEffect(() => {
    setValue('check_in_time', new Date());
    setValue('exchange_rate', usdExchangeRateData);
    setValue('currency', booking.currency);
    if (hotel) {
      Object.entries(hotel).forEach(([key, value]) => {
        if ((key === 'check_in_time' || key === 'check_out_time')) {
          if (value) setValue(key, new Date(value));
        } else setValue(key, value);
      });
    }
    // seed only when booking/hotel change; re-running when the exchange rate loads would overwrite the user's edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, booking, hotel]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (formData) => {
    let data = { ...formData, files };
    if (transit) data = { ...data, transit_id: transit.id };
    try {
      if (hotel) dispatch(updateBookingHotel({ bookingId, hotelId: hotel.id, data }));
      else dispatch(createBookingHotel({ bookingId, data }));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={<><FaHotel className="tw-text-blue-600" /> {title} Hotel booking</>}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="hotel-form" variant="primary">Save</Button>
        </>
      )}
    >
      <Alert type="warning" className="tw-mb-3">Book a hotel for the trip.</Alert>

      <form id="hotel-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Name" required error={errors.name?.message}>
          <input placeholder="Name of hotel" className={inputClass} {...register('name', { required: 'Name of hotel must be required.' })} />
        </FormRow>

        <FormRow label="Check in" required error={errors.check_in_time?.message}>
          <Controller control={control} name="check_in_time" rules={{ required: 'Check in time must be required.' }}
            render={({ field }) => (
              <DatePicker selected={field.value} onChange={(d) => field.onChange(d)} className={inputClass}
                placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight />
            )} />
        </FormRow>

        <FormRow label="Check out" required error={errors.check_out_time?.message}>
          <Controller control={control} name="check_out_time"
            rules={{ required: 'Check out time must be required.', validate: { afterFromDate: (v) => isAfter(v, getValues('check_in_time')) || 'Check out must be after check in time.' } }}
            render={({ field }) => (
              <DatePicker selected={field.value} onChange={(d) => field.onChange(d)} className={inputClass}
                placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight />
            )} />
        </FormRow>

        <FormRow label="Telephone" required error={errors.tel?.message}>
          <input placeholder="Telephone" className={inputClass} {...register('tel', { required: 'Telephone must be required.' })} />
        </FormRow>

        <FormRow label="Address" required error={errors.address?.message}>
          <textarea rows={5} placeholder="Address of hotel" className={inputClass} {...register('address', { required: 'Address must be required.' })} />
        </FormRow>

        <hr className="tw-border-slate-200" />

        <FormRow label="Amount currency" required error={errors.cost_currency?.message}>
          <input placeholder="Amount currency" className={inputClass} {...register('cost_currency', { required: 'Amount currency must be required.' })} />
        </FormRow>

        <FormRow label="Currency" error={errors.currency?.message}>
          <CurrencySelect value={watch('currency')} onChange={(v) => setValue('currency', v, { shouldValidate: true })} />
          <div className={hint}><FaInfoCircle /> Defaults to the trip&apos;s currency; change if this item uses another.</div>
        </FormRow>

        {showExchangeRate && (
          <FormRow label="Exchange rate" error={errors.exchange_rate?.message}>
            <input type="text" placeholder="Exchange rate" className={inputClass} {...register('exchange_rate', { required: 'Exchange rate must be required' })} />
            <Alert type="info" className="tw-mt-2">USD exchange rate to VND from <a href="https://www.vietcombank.com.vn/KHCN/Cong-cu-tien-ich/Ty-gia?devicechannel=default" target="_blank" rel="noreferrer" className="tw-underline">Vietcombank</a>.</Alert>
          </FormRow>
        )}

        <FormRow label="Note">
          <Controller control={control} name="notes"
            render={({ field }) => <TinymceEditor height="30vh" handleChange={field.onChange} value={field.value} />} />
        </FormRow>

        <FormRow label="Attachments">
          <BookingUploadAttachment files={files} setFiles={setFiles} />
        </FormRow>
      </form>
    </Drawer>
  );
};

HotelForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  booking: PropTypes.object.isRequired,
  hotel: PropTypes.object,
  transit: PropTypes.object,
};

export default HotelForm;
